import { lazy, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import UserLayout from '../layouts/UserLayout'
import {
    makeSelectOrgType,
    selectUserAccountTypeCheck,
} from '../redux/userSlice'
import { AuthRoute, tabNames } from './helpers'

import { TabNames } from './type'
import LoginPage from '../pages/LoginPage'
import SignupPage from '../pages/SignupPage'
import landingRoutes from './landingRoutes'

import onboardingRoutes from './onboardingRoutes'
import { selectOrg, setOrg } from '../redux/orgSlice'
import { OrgSelect } from '../components/Header/components/OrgSelect'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { authMiddleware } from '../middleware'
import { deleteAuthMiddleware } from '../middleware/auth'
import MobileFallback from '../pages/Dashboard/MobileFallback'
import { logger } from '../helpers/logger'
import { GENERAL_API } from '../firebase/general-apis'
import { getError } from '../helpers/utils'
import {
    CandidateRefereeSurveyRequest,
    EmploymentDeclarationForDoE,
} from '../firebase/types'
import { DefaultErrorFallback } from '../components/ErrorFallback/ErrorFallback'
// import AppRedirect from '../pages/DocApi/AppRedirect'
import { captureException } from '@sentry/react'
import { SCHOOL_LOADING_ERROR_CODES } from '../components/LoadingSpinner/loadingErrorCodes'
import LoadingErrorPreview from '../components/LoadingSpinner/LoadingErrorPreview'
import {
    isRefereeSurveyNetworkError,
    RefereeSurveyErrorReason,
} from '../pages/RefereeSurveyPage/v3.helper'

import teacherRoutes from './dashboard/teacherRoutes'
import schoolRoutes from './dashboard/school/routes'
import eccRoutes from './dashboard/centre/routes'
import { principalReferenceSurveyRoute } from '../pages/PrincipalReferenceSurvey/route'

const DashboardLayout = lazy(() =>
    import('../layouts/DashboardLayout').then((m) => ({ default: m.default }))
)
const PageNotFound = lazy(() =>
    import('../components/ErrorFallback/PageNotFound').then((m) => ({
        default: m.default,
    }))
)
const AccountDeletePage = lazy(() =>
    import('../pages/AccountDelete').then((m) => ({ default: m.default }))
)

const AppRedirect = lazy(() =>
    import('../pages/DocApi/AppRedirect').then((m) => ({ default: m.default }))
)
const DocApi = lazy(() =>
    import('../pages/DocApi').then((m) => ({ default: m.default }))
)
const PayslipURL = lazy(() =>
    import('../pages/DocApi/PayslipURL').then((m) => ({ default: m.default }))
)

const TeacherApplicationPage = lazy(() =>
    import('../pages/TeacherApplicationPage/v2').then((m) => ({
        default: m.default,
    }))
)

const RefereeSurveyPage = lazy(() =>
    import('../pages/RefereeSurveyPage/v3').then((m) => ({
        default: m.default,
    }))
)

const AppIndexRedirect = () => {
    const accountType = useSelector(selectUserAccountTypeCheck)

    logger.debug(`Redirect mounted: ${accountType}`)

    if (accountType === undefined) {
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                errorCode={SCHOOL_LOADING_ERROR_CODES.ACCOUNT_TYPE.code}
            />
        )
    }

    switch (accountType) {
        case 'teacher':
            return <Navigate to="teacher" replace />
        case 'school':
        case 'childcareCentre':
        case 'organisation':
            return <OrgRedirect />
        default:
            logger.debug(`navigating to onboarding`)
            return <Navigate to={`/${TabNames.ON_BOARDING}`} replace />
    }
}

const OrgRedirect = () => {
    const accountType = useSelector(selectUserAccountTypeCheck)

    const selectOrgType = useMemo(makeSelectOrgType, [])
    const org = useSelector(selectOrgType)

    const localOrg = useSelector(selectOrg)

    const dispatch = useDispatch()

    // If redux schoolUid is not set, use selectedSchoolId
    useEffect(() => {
        if (org === null) return

        if (localOrg === undefined) {
            dispatch(setOrg(org))
        } else if (
            accountType !== 'organisation' &&
            localOrg?.type !== accountType
        ) {
            dispatch(setOrg(org))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localOrg, org])

    if (localOrg === undefined) {
        if (org === null) {
            return (
                <Stack
                    sx={{
                        height: '100vh',
                        width: '100vw',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                    }}
                >
                    <Typography>
                        Please select the organisation to access
                    </Typography>
                    <Divider flexItem />
                    <Box
                        sx={{
                            width: 400,
                            maxWidth: '100%',
                        }}
                    >
                        <OrgSelect
                            selectProps={{
                                fullWidth: true,
                                label: 'Select your organisation',
                            }}
                            controlProps={{
                                fullWidth: true,
                            }}
                        />
                    </Box>
                </Stack>
            )
        }
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                id="org-redirect-localOrg-wait"
                errorCode={SCHOOL_LOADING_ERROR_CODES.ACTIVE_ORGANISATION.code}
            />
        )
    }

    if (localOrg.type === 'school') {
        return <Navigate to={`school/${localOrg.id}`} replace />
    }
    if (localOrg.type === 'childcareCentre') {
        return <Navigate to={`centre/${localOrg.id}`} replace />
    }
}

export const routes = createBrowserRouter([
    {
        element: <UserLayout />,
        errorElement: <DefaultErrorFallback />,
        HydrateFallback: () => (
            <LoadingSpinner fillViewPortHeight fillViewPortWidth />
        ),
        children: [
            {
                path: '__debug/loading',
                element: <LoadingErrorPreview />,
            },
            landingRoutes,

            { path: 'download', element: <MobileFallback /> },

            {
                path: 'dashboard',
                middleware: [authMiddleware],
                element: <DashboardLayout />,
                children: [
                    { index: true, element: <AppIndexRedirect /> },
                    teacherRoutes,
                    schoolRoutes,
                    eccRoutes,
                    {
                        path: '*',
                        element: (
                            <PageNotFound homeLink={`/${tabNames.app.index}`} />
                        ),
                    },
                ],
            },

            // NOTE: Disable email verification
            // {
            //     path: tabNames.auth.verify,
            //     element: <VerifyWrapper />,
            //     children: [{ index: true, element: <VerifyEmailPage /> }],
            //     handle: {
            //         meta: {},
            //     },
            // },
            {
                element: <AuthRoute />,
                children: [
                    {
                        path: tabNames.auth.login,
                        children: [
                            {
                                index: true,
                                element: <LoginPage />,
                                handle: {
                                    meta: {
                                        title: 'Login - Spark Relief Teachers',
                                        description:
                                            'Sign in to your Spark Relief Teachers account here.',
                                    },
                                },
                            },
                            {
                                path: 'reset-password',
                                element: <LoginPage />,
                                handle: {
                                    meta: {
                                        title: 'Reset Password - Spark Relief Teachers',
                                        description:
                                            'Request a password reset to your Spark Relief Teachers account here.',
                                    },
                                },
                            },
                        ],
                    },
                    {
                        path: tabNames.auth.register,
                        element: <SignupPage />,
                        handle: {
                            meta: {
                                title: 'Register - Spark Relief Teachers',
                                description:
                                    'Register your Spark Relief Teachers account here.',
                            },
                        },
                    },
                ],
            },

            {
                path: tabNames.employmentReview,
                children: [
                    { index: true, element: <Navigate to={`/`} /> },
                    {
                        path: ':teacherId',
                        loader: async ({
                            params,
                        }): Promise<{
                            declaration: EmploymentDeclarationForDoE | null
                        }> => {
                            try {
                                if (!params.teacherId)
                                    throw new Error('Teacher UUID')
                                const data = (
                                    await GENERAL_API.fetchEmploymentReview(
                                        params.teacherId
                                    )
                                ).val()

                                if ('formData' in data) {
                                    return {
                                        declaration: {
                                            ...data,
                                            version: 2,
                                        },
                                    }
                                } else {
                                    return {
                                        declaration: {
                                            version: 1,
                                            formData: {
                                                name: data.name || '',
                                                dob: data.dob || '',
                                                otherNames:
                                                    data.otherNames || '',
                                                vitNo: data.vitNo || '',
                                            },
                                            timeDeclared:
                                                data.timeDeclared || undefined,
                                        },
                                    }
                                }
                            } catch (e) {
                                logger.debug(getError(e))
                                return { declaration: null }
                            }
                        },
                        element: <TeacherApplicationPage />,
                        errorElement: <Stack>Unable to process request</Stack>,
                    },
                ],
                handle: {
                    meta: {
                        title: `Teacher Application - Spark Relief Teachers`,
                    },
                },
            },
            {
                path: tabNames.refereeSurvey,
                children: [
                    { index: true, element: <Navigate to={`/`} /> },
                    {
                        path: ':refereeId',
                        loader: async ({
                            params,
                        }): Promise<{
                            data: CandidateRefereeSurveyRequest | null
                            errorReason?: RefereeSurveyErrorReason
                        }> => {
                            try {
                                if (!params.refereeId)
                                    throw new Error('Missing referee UUID')
                                const snap =
                                    await GENERAL_API.referenceSurvey.get(
                                        params.refereeId
                                    )
                                if (!snap.exists()) {
                                    return { data: null }
                                }
                                try {
                                    await GENERAL_API.referenceSurvey.updateVisitedTimeStamp(
                                        params.refereeId
                                    )
                                } catch (e) {
                                    logger.warn(
                                        `Failed to update visited timestamp: ${getError(e)}`
                                    )
                                    captureException(e, {
                                        tags: {
                                            feature: 'Referee Survey',
                                            eventType:
                                                'referee_visited_timestamp_update_error',
                                            route: '/referee/:refereeId',
                                            refereeId: params.refereeId,
                                        },
                                    })
                                }

                                return {
                                    data: snap.val(),
                                }
                            } catch (e) {
                                logger.debug(getError(e))
                                captureException(e, {
                                    tags: {
                                        feature: 'Referee Survey',
                                        eventType:
                                            'referee_survey_loader_error',
                                        errorReason:
                                            isRefereeSurveyNetworkError(e)
                                                ? 'network-error'
                                                : 'survey-unavailable',
                                        route: '/referee/:refereeId',
                                        refereeId:
                                            params.refereeId ?? 'missing',
                                    },
                                })
                                return {
                                    data: null,
                                    errorReason: isRefereeSurveyNetworkError(e)
                                        ? 'network-error'
                                        : 'survey-unavailable',
                                }
                            }
                        },
                        element: <RefereeSurveyPage />,
                    },
                ],
                handle: {
                    meta: {
                        title: `Reference Survey - Spark Relief Teachers`,
                    },
                },
            },
            principalReferenceSurveyRoute,
            {
                path: tabNames.docs,
                children: [
                    { index: true, element: <Navigate to={`/`} /> },
                    { path: ':docSlug', element: <DocApi /> },
                ],
                handle: {
                    meta: {
                        title: 'Fetching document...',
                    },
                },
            },
            {
                path: tabNames.payslips,
                children: [
                    { index: true, element: <Navigate to={`/`} /> },
                    { path: ':uid/:fYear/:payslipId', element: <PayslipURL /> },
                ],
                handle: {
                    meta: {
                        title: 'Fetching document...',
                    },
                },
            },
            {
                path: '/app',
                element: <AppRedirect />,
                handle: {
                    meta: {
                        title: 'Redirecting...',
                    },
                },
            },

            onboardingRoutes,

            {
                path: 'account-delete',
                middleware: [deleteAuthMiddleware],
                element: <AccountDeletePage />,
            },
            // Catch unknown paths
            {
                path: '*',
                element: <PageNotFound />,
            },
        ],
    },
])
